import axios from "axios";
import Cookies from "js-cookie";
import { debounce } from "lodash";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Author, AuthorResponse } from "../../../types/author";
import { adminTokenCookieName, envs } from "../../../utils/constants";
import { Widget } from "../../../widgetList/widgetList";

interface Props {
	choosenItem: Widget;
	onUpdate: (w: Widget) => void;
	apiUrl?: string;
}

const AuthorListWidget: FC<Props> = ({ choosenItem, onUpdate }) => {
	// State for managing component data
	const [active, setActive] = useState(choosenItem?.active || false);
	const [searchText, setSearchText] = useState("");
	const [authors, setAuthors] = useState<Author[]>([]);
	const [selectedAuthors, setSelectedAuthors] = useState<
		Pick<Author, "id" | "name" | "slug" | "image">[]
	>(choosenItem?.data?.authors || []);
	const [showDropdown, setShowDropdown] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [pagination, setPagination] = useState({
		currentPage: 1,
		totalPage: 1,
		perPage: 10,
	});

	const dropdownRef = useRef<HTMLDivElement>(null);

	// Handle click outside dropdown
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setShowDropdown(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	// Fetch authors based on search and pagination
	const fetchAuthors = useCallback(
		async (search: string, page: number = 1) => {
			setLoading(true);
			setError(null);
			try {
				const response = await axios.get(
					`${envs.baseApi}author?perPage=10&currentPage=${page}&search=${search}`,
					{
						headers: {
							Authorization: `Bearer ${Cookies.get(
								adminTokenCookieName
							)}`,
						},
					}
				);

				const data: AuthorResponse = await response.data;
				setAuthors(data.data);
				setPagination({
					currentPage: data.currentPage,
					totalPage: data.totalPage,
					perPage: data.perPage,
				});
			} catch (err) {
				setError(
					err instanceof Error
						? err.message
						: "An unknown error occurred"
				);
				console.error("Error fetching authors:", err);
			} finally {
				setLoading(false);
			}
		},
		[]
	);

	// Debounced search handler
	const debouncedSearch = useCallback(
		debounce((searchValue: string) => {
			fetchAuthors(searchValue);
		}, 300),
		[fetchAuthors]
	);

	// Effect to load authors when component mounts
	useEffect(() => {
		fetchAuthors("");
	}, [fetchAuthors]);

	// Handle search input change
	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSearchText(value);
		debouncedSearch(value);
	};

	// Select an author
	const handleSelectAuthor = (author: Author) => {
		const { id, name, slug, image } = author;
		// Check if author already selected to avoid duplicates
		if (!selectedAuthors.some((selected) => selected.id === id)) {
			setSelectedAuthors([{ id, name, slug, image }, ...selectedAuthors]);
		}
		setSearchText("");
		setShowDropdown(false);
	};

	// Remove a selected author
	const handleRemoveAuthor = (index: number) => {
		const updatedAuthors = [...selectedAuthors];
		updatedAuthors.splice(index, 1);
		setSelectedAuthors(updatedAuthors);
	};

	// Sort selected authors
	const handleSortAuthors = (
		authorList: Pick<Author, "id" | "name" | "slug" | "image">[]
	) => {
		setSelectedAuthors(authorList);
	};

	// Form submission
	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		const updatedWidget = {
			...choosenItem,
			active,
			data: {
				authors: selectedAuthors,
			},
		};
		onUpdate(updatedWidget);
	};

	// Handle pagination
	const handlePageChange = (newPage: number) => {
		if (newPage > 0 && newPage <= pagination.totalPage) {
			fetchAuthors(searchText, newPage);
		}
	};

	// Helper method to get image URL
	const getImageUrl = (imagePath: string) => {
		if (!imagePath) return "https://via.placeholder.com/150?text=No+Image";
		return `${process.env.REACT_APP_IMAGE_URL || ""}${imagePath}`;
	};

	return (
		<div className="p-6 bg-white rounded-lg shadow-lg max-w-4xl mx-auto">
			<form onSubmit={handleSubmit} className="space-y-6">
				<div className="border-b pb-4">
					<h1 className="text-2xl font-bold text-gray-800">
						Author List Widget
					</h1>
					<p className="text-gray-500 mt-1">
						Select and organize authors to display in your widget
					</p>
				</div>

				<div className="flex items-center gap-2">
					<div className="relative inline-flex items-center cursor-pointer">
						<input
							type="checkbox"
							className="sr-only peer"
							checked={active}
							onChange={() => setActive(!active)}
							id="toggleActive"
						/>
						<div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
						<label
							htmlFor="toggleActive"
							className="ms-3 text-sm font-medium text-gray-900">
							Widget Active
						</label>
					</div>
				</div>

				{/* Search Bar */}
				<div className="relative" ref={dropdownRef}>
					<div className="mb-2">
						<label
							htmlFor="authorSearch"
							className="block text-sm font-medium text-gray-700">
							Search Authors
						</label>
						<div className="mt-1 relative">
							<input
								type="text"
								id="authorSearch"
								className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-3"
								placeholder="Type author name to search..."
								value={searchText}
								onChange={handleSearchChange}
								onFocus={() => setShowDropdown(true)}
							/>
							{loading && (
								<div className="absolute inset-y-0 right-0 flex items-center pr-3">
									<svg
										className="animate-spin h-5 w-5 text-gray-400"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24">
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"></circle>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
									</svg>
								</div>
							)}
						</div>
					</div>

					{/* Authors Dropdown */}
					{showDropdown && (
						<div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
							{error && (
								<div className="px-4 py-2 text-sm text-red-500">
									{error}
								</div>
							)}

							{!loading && !error && authors.length === 0 && (
								<div className="px-4 py-2 text-sm text-gray-700">
									No authors found
								</div>
							)}

							{authors.map((author) => (
								<div
									key={author.id}
									className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
									onClick={() => handleSelectAuthor(author)}>
									<div className="flex-shrink-0 h-10 w-10">
										<img
											className="h-10 w-10 rounded-full object-cover"
											src={getImageUrl(author.image)}
											alt={author.name}
											onError={(e) => {
												(
													e.target as HTMLImageElement
												).src =
													"https://via.placeholder.com/150?text=No+Image";
											}}
										/>
									</div>
									<div className="ml-4 flex-1">
										<div className="text-sm font-medium text-gray-900">
											{author.name}
										</div>
										<div className="text-sm text-gray-500">
											{author.slug}
										</div>
									</div>
								</div>
							))}

							{/* Pagination */}
							{pagination.totalPage > 1 && (
								<div className="px-4 py-2 border-t border-gray-100 flex justify-between items-center">
									<button
										type="button"
										onClick={() =>
											handlePageChange(
												pagination.currentPage - 1
											)
										}
										disabled={pagination.currentPage === 1}
										className="text-sm text-blue-500 disabled:text-gray-400 disabled:cursor-not-allowed">
										Previous
									</button>
									<span className="text-sm text-gray-500">
										Page {pagination.currentPage} of{" "}
										{pagination.totalPage}
									</span>
									<button
										type="button"
										onClick={() =>
											handlePageChange(
												pagination.currentPage + 1
											)
										}
										disabled={
											pagination.currentPage ===
											pagination.totalPage
										}
										className="text-sm text-blue-500 disabled:text-gray-400 disabled:cursor-not-allowed">
										Next
									</button>
								</div>
							)}
						</div>
					)}
				</div>

				{/* Selected Authors */}
				{selectedAuthors.length > 0 && (
					<div className="space-y-3">
						<div className="flex justify-between items-center">
							<h3 className="text-lg font-medium text-gray-900">
								Selected Authors
							</h3>
							<button
								type="button"
								onClick={() => {
									// This would connect to your sorting component in a real implementation
									// For now, we'll just reverse the order as a demo
									handleSortAuthors(
										[...selectedAuthors].reverse()
									);
								}}
								className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
								<svg
									className="mr-2 -ml-0.5 h-4 w-4"
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
									/>
								</svg>
								Sort Authors
							</button>
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
							{selectedAuthors.map((author, index) => (
								<div
									key={`selected-${author.id}`}
									className="relative flex items-center space-x-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm hover:shadow transition-shadow duration-200">
									<div className="flex-shrink-0">
										<img
											className="h-10 w-10 rounded-full object-cover border border-gray-200"
											src={getImageUrl(author.image)}
											alt={author.name}
											onError={(e) => {
												(
													e.target as HTMLImageElement
												).src =
													"https://via.placeholder.com/150?text=No+Image";
											}}
										/>
									</div>
									<div className="min-w-0 flex-1">
										<p className="truncate text-sm font-medium text-gray-900">
											{author.name}
										</p>
										<p className="truncate text-sm text-gray-500">
											{author.slug}
										</p>
									</div>
									<button
										type="button"
										onClick={() =>
											handleRemoveAuthor(index)
										}
										className="absolute -top-2 -right-2 h-6 w-6 inline-flex items-center justify-center rounded-full bg-white border border-gray-200 shadow-sm text-red-500 hover:text-red-700 focus:outline-none">
										<span className="sr-only">
											Remove author
										</span>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											className="h-4 w-4"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor">
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M6 18L18 6M6 6l12 12"
											/>
										</svg>
									</button>
								</div>
							))}
						</div>
					</div>
				)}

				<div className="flex justify-end pt-4">
					<button
						type="submit"
						className="inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200">
						Save Changes
					</button>
				</div>
			</form>
		</div>
	);
};

export default AuthorListWidget;
